import { Button, styled } from "@mui/material";
import { ediphiPalette } from "../../styles/colors";

interface CardButtonProps {
  $selected?: boolean;
}

export const CardButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== "$selected",
})<CardButtonProps>(({ theme, $selected }) => {
  const activeStyles = {
    color: ediphiPalette.brand600,
    boxShadow: `inset 0 0 0 2px ${ediphiPalette.brand600}`,
    background: "none",
    "& .MuiTypography-root": { color: "inherit" },
  };

  return {
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    paddingTop: theme.spacing(1),
    paddingBottom: "6px",
    borderRadius: "8px",
    // The theme pins every Button to height: 44px, which stops this one growing with its content —
    // taller content then overflows the border instead, since ButtonBase centres it. minHeight keeps
    // the floor.
    height: "auto",
    minHeight: "54px",
    color: ediphiPalette.textStrong,
    // boxShadow: `inset 0 0 0 1px ${ediphiPalette.borderInput}`,
    boxShadow: `inset 0 0 0 1px ${ediphiPalette.borderColor}`,
    "&:hover": activeStyles,
    "&.Mui-disabled": {
      // Not hoistable to the theme: it has to outrank both the `&:hover` rule above and the
      // `$selected` spread below, and a theme override loses to each.
      //
      // surfaceSubtle, not surfaceDisabled: that sits five units from the page background, so a
      // disabled row on the page lost the fill contrast defining it and the read-only exports on
      // User Settings ran together into one slab. This reads as recessed against white while staying
      // clear of the page — the same fill-plus-hairline pairing EmptyStateCard uses on that ground.
      backgroundColor: ediphiPalette.surfaceSubtle,
      color: ediphiPalette.textDisabled,
      // Restated unconditionally, not left to the root rule: `&:hover` above sets a boxShadow at
      // equal specificity, so without this a disabled row still takes the brand ring on hover.
      boxShadow: `inset 0 0 0 1px ${ediphiPalette.borderColor}`,
      // MUI eases background-color, box-shadow, border-color and colour over 250ms. That is right for
      // hover, which anticipates, and wrong for this, which reports: clicking a row you are hovering
      // left its ring fading from the brand blue and its label from black for a quarter second after
      // the spinner had already appeared. Cleared here only — every other state keeps MUI's easing,
      // and the label follows because it inherits this colour.
      transition: "none",
      // Only the button that is disabled *because it is the one being acted on* keeps a ring, in
      // grey: this rule out-specifies the $selected spread below, so it swaps the brand outline
      // out rather than letting it read as still active. Everything else disabled — an option
      // already taken — stays on the hairline instead of gaining weight.
      ...($selected && { boxShadow: `inset 0 0 0 2px ${ediphiPalette.ediphiSelectBorder}` }),
    },
    ...($selected && activeStyles),
  };
});
